import { useState } from "react";

import { Button, Callout } from "@blueprintjs/core";
import capitalize from "lodash/capitalize";

import { Box } from "@/components/layout/flexbox";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  UpdateMemberRoleMutation,
  useUpdateMemberRoleMutation,
} from "@/graphql";

interface ResultsProps {
  result: UpdateMemberRoleMutation["updateMemberRole"];
  onClose: () => void;
}

function Results({ result, onClose }: ResultsProps) {
  const resultErrorMessage = result?.errors?.[0]?.message;
  const resultView =
    result && result.ok ? (
      <Callout intent="success" icon="tick">
        Update successfully
      </Callout>
    ) : (
      <Callout intent="warning">
        {resultErrorMessage ||
          "We could not process your request at this time."}
      </Callout>
    );
  return (
    <Box>
      {resultView}
      <Box mt={16}>
        <Button
          intent="primary"
          large={true}
          onClick={() => onClose()}
          loading={false}
        >
          Close
        </Button>
      </Box>
    </Box>
  );
}

interface UpdateUserRoleDialogProps {
  open: boolean;
  dto?: {
    userName: string;
    memberId: string;
    currentRole: string;
    newRole: string;
    shouldReload: boolean;
  };
  onClose: () => void;
}

export function UpdateUserRoleDialog({
  open,
  dto,
  onClose,
}: UpdateUserRoleDialogProps) {
  const [result, setResult] =
    useState<UpdateMemberRoleMutation["updateMemberRole"]>();

  const [updateMemberRole, { loading }] = useUpdateMemberRoleMutation({
    onCompleted: (data) => {
      const result = data.updateMemberRole;
      setResult(result);
      if (result && result.ok && dto?.shouldReload) {
        window.location.reload();
      }
    },
    onError: () => {
      setResult({ ok: false });
    },
  });

  if (!dto) {
    return null;
  }

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title="Convert user role"
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setResult(undefined);
      }}
    >
      <Box py={3} px="24px">
        {result ? (
          <Results result={result} onClose={onClose} />
        ) : (
          <Box>
            <Box css={{ padding: "10px 0px", color: SUBTEXT_COLOR }}>
              User <b>{dto.userName}</b> will be converted from{" "}
              {capitalize(dto.currentRole)} to <b>{capitalize(dto.newRole)}</b>
            </Box>
            <Box mt={3}>
              <Button
                intent="primary"
                large={true}
                disabled={loading}
                onClick={() => {
                  updateMemberRole({
                    variables: {
                      memberId: dto.memberId,
                      role: dto.newRole,
                    },
                  });
                }}
                loading={loading}
              >
                Confirm
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </StyledDialog>
  );
}
